import React from 'react';
import type { UrlObject } from 'url';

import { FilterParams } from '../hooks/useSearchParameter';

interface JobTrackingPayload {
  linkText: string;
  linkType: 'LinkActionLink';
  experienceLevel: string;
  supplier: string;
  jobId?: number;
  jobOwner?: string;
  jobModel?: string;
}

export interface AppConfig {
  // BEHAVIOUR

  // Run dry query on homeRoute: (boolean: true/false)
  showImmediateSearchResults?: string;
  // Show search filters: (boolean: true/false)
  showSearchFilters?: string;
  // preset search filters
  searchFilters?: FilterParams | null;

  // ADDITIONAL SEARCH FILTER

  // additional filter: bank code
  filterBankCode?: number | string;
  // additional filter: union id
  filterUnion?: number;

  // ROUTING

  // prefix urls with #: (boolean: true/false)
  useHashRouter?: string;

  jobMarketBasePath: string;
  jobMarketSearchRoute: string;
  jobMarketSearchRouteParams: string;
  jobMarketDetailsRoute: string;
  jobMarketDetailsRouteParams: string;
  jobMarketCareerRoute: string;
  jobMarketTargetPageRoute: string;
  jobMarketTargetPageRouteParams: string;
  jobMarketAboutUsRoute: string;

  // API CONFIG

  jobMarketApiUrl: string;

  jobMarketApiKey?: string;

  jobMarketLogoPath: string;

  highlightPremium: boolean;

  jobFileApiUrl: string;

  // Sitekey setting for FriendlyCaptcha
  friendlyCaptchaSitekey?: string;
  // Endpoint URL setting for FriendlyCaptcha
  friendlyCaptchaEndpointUrl?: string;

  // base url to link to job provider pages
  jobProviderPageUrl?: string;

  internPrivacyUrl: string;

  youtubePrivacyUrl: string;

  // reCaptchaKey
  reCaptchaKey?: string;

  // url for tiles
  yellowmapTileUrl: string;
  yellowmapApiUrl: string;
  yellowmapAutocompleteUrl: string;
  yellowmapSystemPartner: string;
  yellowmapApiKey: string;
  yellowmapFrontendKey: string;

  // override for default navigation behaviour
  onRouteChange?: (url: UrlObject | string, options?: unknown, method?: 'push' | 'replace') => void;
  // url/path for maps fallback image
  mapImageFallbackUrl?: string;

  // url for the data privacy statement
  consentHref?: string;

  showCompanyFilter: boolean;

  showLogo?: boolean;

  // link tracking in jobMarket
  handleSearchTracking?: (payload: { linkText: string; linkType: 'LinkActionLink' }) => void;
  handleOfferClickTracking?: (payload: JobTrackingPayload) => void;
  handleApplicationTracking?: (payload: JobTrackingPayload) => void;
  handleApplicationExternalTracking?: (payload: JobTrackingPayload) => void;
  handleSubmitApplicationTracking?: (payload: JobTrackingPayload) => void;
  handleViewTracking?: (payload: JobTrackingPayload) => void;
}

export const JOBS_APP_CONFIG: AppConfig = {
  showImmediateSearchResults: undefined,
  showSearchFilters: undefined,
  searchFilters: null,

  filterUnion: undefined,
  filterBankCode: undefined,

  useHashRouter: undefined,

  jobMarketBasePath: '/',
  jobMarketSearchRoute: '/',
  jobMarketSearchRouteParams: ':search?',
  jobMarketDetailsRoute: '/jobboerse/jobangebot/',
  jobMarketDetailsRouteParams: ':slug-:id(\\d+).html',
  jobMarketCareerRoute: '/karriere.html',
  jobMarketTargetPageRoute: '/karriere/',
  jobMarketTargetPageRouteParams: ':slug',
  jobMarketAboutUsRoute: '/ueber-uns.html',

  jobMarketApiUrl: '/api/job-market/',
  jobMarketApiKey: '',
  jobMarketLogoPath: '',
  highlightPremium: true,

  jobFileApiUrl: '',

  // eslint-disable-next-line @if6/fi-no-absolute-urls
  jobProviderPageUrl: '/jobboerse/unternehmen/',
  mapImageFallbackUrl: '/assets/map_fallback.png',

  // eslint-disable-next-line @if6/fi-no-absolute-urls
  yellowmapTileUrl: 'https://{s}.yellowmaps.eu/{t}/{z}/{x}/{y}{r}.png',
  yellowmapAutocompleteUrl: '',
  yellowmapSystemPartner: '',
  yellowmapApiKey: '',
  yellowmapFrontendKey: '',
  yellowmapApiUrl: '',

  // eslint-disable-next-line @if6/fi-no-absolute-urls
  internPrivacyUrl: 'https://www.sparkasse.de/datenschutz.html',

  // eslint-disable-next-line @if6/fi-no-absolute-urls
  consentHref: 'https://www.sparkasse.de/datenschutz.html',

  // eslint-disable-next-line @if6/fi-no-absolute-urls
  youtubePrivacyUrl: 'https://policies.google.com/privacy?hl=de',

  reCaptchaKey: '',

  showCompanyFilter: true,

  handleSearchTracking: undefined,
  handleOfferClickTracking: undefined,
  handleApplicationTracking: undefined,
  handleApplicationExternalTracking: undefined,
  handleSubmitApplicationTracking: undefined,
  handleViewTracking: undefined,
  showLogo: false,
};

export const ConfigurationContext = React.createContext<AppConfig>(JOBS_APP_CONFIG);
